import React, {useState, useEffect} from 'react';
import * as C from '../../Common.styles';

import PersonalDetails from './PersonalDetails/PersonalDetails';
import PanCardDetails from './PanCardDetails/PanCardDetails';
import BusinessDetails from './BusinessDetails/BusinessDetails';
import {validations} from '../../../../../utils/validations';
import {NON_EDITABLE_STATUS} from '../../../../../constants';
import {IKYCData} from '../../../../../store/types';
import {sendEvarData} from '@ibibo/analytics';
import ButtonLoader from '../../../UI/Loader/Loader';

const AccountDetails = ({
  data,
  error,
  status,
  submitDetails,
  cancelPAN,
  onUpload,
  businessTypeList,
  setLoader,
  gstLoader,
  mobile,
  editEmail,
}) => {
  const [{title, fullName, email}, setPersonalDetails] = useState({
    title: data.title || '',
    fullName: data.fullName || '',
    email: data.email || '',
  });

  const [
    {agencyName, panNumber, panUploaded, panVerified, panSuccess, pan_name, pan_type, user_msg},
    setPanDetails,
  ] = useState({
    agencyName: data.agencyName || '',
    panNumber: data.panNumber || '',
    panUploaded: data.panUploaded || false,
    panVerified: data.panVerified || false,
    panSuccess: data.panSucces || false,
    pan_name: data.pan_name || '',
    pan_type: data.pan_type || false,
    user_msg: data.user_msg || '',
  });

  const [
    {businessType, address, state, city, pincode, gstNumber, panInfo},
    setBusinessDetails,
  ] = useState({
    businessType: data.businessType || '',
    address: data.address || '',
    state: data.state || '',
    city: data.city || '',
    pincode: data.pincode || '',
    gstNumber: data.gstNumber || '',
    panInfo: data.panInfo || null,
  });

  const [apiErrorMsg, setApiError] = useState(error);
  const [errors, setError] = useState({});
  const [doNotHaveGSTIN, setDoNotHaveGSTIN] = useState(
    panUploaded && gstNumber === '' && !panInfo ? true : false,
  );

  const getHasGSTN = list => {
    for (let i = 0; i < list.length; i++) {
      if (list[i].active_status === 'Active') {
        return true;
      }
    }
    return false;
  };
  const hasGSTINList =
    panInfo && panInfo.gstin_list && panInfo.gstin_list.length
      ? getHasGSTN(panInfo.gstin_list)
      : false;
  const isNoGstErrorState =
    panUploaded && panInfo && !hasGSTINList && !doNotHaveGSTIN && !gstNumber;

  useEffect(() => {
    setApiError(error);
  }, [error]);

  useEffect(() => {
    setDoNotHaveGSTIN(panUploaded && gstNumber === '' && !panInfo ? true : false);
  }, [panUploaded, gstNumber, panInfo]);

  useEffect(() => {
    setPersonalDetails({
      title: data.title || title,
      fullName: data.fullName || fullName,
      email: data.email || email,
    });
    setPanDetails({
      agencyName: data.agencyName || agencyName,
      panNumber: data.panNumber || panNumber,
      panUploaded: data.panUploaded || panUploaded,
      panVerified: data.panVerified || panVerified,
      panSuccess: data.panSuccess || panSuccess,
      pan_name: data.pan_name || pan_name,
      pan_type: data.pan_type || pan_type,
      user_msg: data.user_msg || user_msg,
    });
    setBusinessDetails({
      businessType: data.businessType || businessType,
      address: data.address || address,
      state: data.state || state,
      city: data.city || city,
      pincode: data.pincode || pincode,
      gstNumber: data.gstNumber || gstNumber,
      panInfo: data.panInfo || panInfo,
    });
  }, [data]);

  useEffect(() => {
    if (!data.panUploaded) {
      setPanDetails({
        agencyName: agencyName,
        panNumber: panNumber,
        panUploaded: false,
        panVerified: false,
        panSuccess: false,
        pan_name: '',
        pan_type: false,
        user_msg: '',
      });
    }
  }, [data.panUploaded]);

  useEffect(() => {
    setBusinessDetails({
      businessType: businessType,
      address: address,
      state: state,
      city: city,
      pincode: pincode,
      gstNumber: doNotHaveGSTIN ? '' : gstNumber,
      panInfo: null,
    });
  }, [doNotHaveGSTIN]);

  const fieldValidators = {
    agencyName: {fieldName: 'Agency Name', validators: ['required', 'validAddress']},
    panNumber: {fieldName: 'PAN', validators: ['required', 'validPAN']},
    businessType: {fieldName: 'Business Type', validators: ['required']},
    address: {fieldName: 'Address', validators: ['required', 'validAddress']},
    state: {fieldName: 'State', validators: ['required']},
    city: {fieldName: 'City', validators: ['required']},
    pincode: {fieldName: 'Pincode', validators: ['required', 'validPincode']},
    gstNumber: {fieldName: 'GST Number', validators: []},
    kycDocType: {fieldName: 'KYC Document Type', validators: []},
    kycDoc: {fieldName: 'KYC Document', validators: []},
    facadeDoc: {fieldName: 'Facade Document', validators: []},
    panDoc: {fieldName: 'PAN Document', validators: ['required']},
    additionalDoc: {fieldName: 'Additional KYC Document Type', validators: []},
    title: {fieldName: 'Title', validators: ['required']},
    fullName: {fieldName: 'Name', validators: ['required', 'alpha']},
    email: {fieldName: 'Email', validators: ['required', 'validEmail']},
  };

  const isGSTBlockEnabled = agencyName !== '' && panNumber !== '' && panUploaded ? true : false;
  // const isGSTBlockEnabled = true;

  const validateFields = data => {
    let errorObj = {};
    // if (!doNotHaveGSTIN) {
    //     fieldValidators.gstNumber.validators.push('required');
    //     fieldValidators.gstNumber.validators.push('validGSTNumber');
    // }
    Object.keys(data).map(key => {
      if (fieldValidators[key]) {
        const validators = fieldValidators[key].validators;
        for (let i = 0; i < validators.length; i++) {
          const validateFunction = validations[validators[i]];
          const resp = validateFunction({
            value: data[key],
            fieldName: fieldValidators[key].fieldName,
          });
          if (resp && !errorObj[key]) {
            errorObj[key] = resp;
          }
        }
      }
    });
    setError(errorObj);
    if (Object.keys(errorObj).length) {
      return true;
    }
    return false;
  };

  const submitInfo = (event: React.FormEvent) => {
    event.preventDefault();
    let dataObj: IKYCData = {
      title,
      fullName,
      email,
      agencyName,
      panNumber,
      panDoc: panUploaded,
    };
    if (panUploaded) {
      dataObj = {
        title,
        fullName,
        email,
        agencyName,
        panNumber,
        panDoc: panUploaded,
        gstNumber,
        businessType,
        address,
        state,
        city,
        pincode,
        doNotHaveGSTIN,
      };
    }
    const hasError = validateFields(dataObj);
    if (!hasError) {
      submitDetails(dataObj);
    }
    sendEvarData(
      {
        prop54: 'myPartner|signup|businessVerification|next',
      },
      true,
    );
  };

  const updatePersonalDetails = data => {
    setPersonalDetails(data);
  };

  const updatePanDetails = data => {
    setPanDetails(data);
  };

  const updateBusinessDetails = data => {
    setBusinessDetails(data);
  };

  const submitPanDetails = ({panNumber, agencyName, panFile, successCallBack}) => {
    onUpload({panNumber, agencyName, panFile, successCallBack, fullName});
  };

  return (
    <C.PopContentMainWrap>
      <C.PopContentHeadWrap>
        <C.PopContentHeadText>Create myPartner Account</C.PopContentHeadText>
        <C.PopContentSubHeadText>Tell us a little bit about your business</C.PopContentSubHeadText>
      </C.PopContentHeadWrap>
      <C.PopContentInnerWrap>
        <C.UserInfoForm onSubmit={submitInfo}>
          <PersonalDetails
            data={data}
            errors={errors}
            updateData={updatePersonalDetails}
            mobile={mobile}
            editEmail={editEmail}
          />
          <PanCardDetails
            onUpload={submitPanDetails}
            cancelPAN={cancelPAN}
            data={data}
            errors={errors}
            updateData={updatePanDetails}
            apiError={apiErrorMsg}
            status={status}
          />
          <BusinessDetails
            fieldValidators={fieldValidators}
            data={data}
            updateBusinessDetails={updateBusinessDetails}
            errors={errors}
            businessTypeList={businessTypeList}
            gstEnabled={isGSTBlockEnabled}
            doNotHaveGSTIN={doNotHaveGSTIN}
            onDoNotHaveGSTINChange={setDoNotHaveGSTIN}
          />
          <C.ButtonWrap>
            <C.ContinueButton
              disabled={setLoader || gstLoader || isNoGstErrorState}
              type="submit"
            >
              {setLoader || gstLoader ? <ButtonLoader loaderType="button" /> : 'Continue'}
            </C.ContinueButton>
          </C.ButtonWrap>
        </C.UserInfoForm>
      </C.PopContentInnerWrap>
    </C.PopContentMainWrap>
  );
};

export default AccountDetails;
